import React, { useContext, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { LayoutContext } from "Components/LayoutContext";
import { Modal, ModalBody, ModalFooter, ModalHeader, Input, Button, SpinningButton } from "Components/ReactstrapSlim";
import { PayableKey, PayableType, PaymentMethodClient, PaymentMethodViewModel, PaymentMethodListViewModel, PaymentMethodRequest, PaymentProviderType, CreditCardPaymentMethodViewModel, BankAccountPaymentMethodViewModel } from "Services/API/API";
import { useApiClient } from "Services/API/useApiClient";
import PaymentMethodFormBrainTree from "./PaymentMethodFormBrainTree";
import { useBankAccount } from "Hooks";
import classes from "./PaymentSettings.module.scss";

export interface PaymentSettingsProps {
    setLoading: (isLoading: boolean) => void;
    id: Guid;
    payableType: PayableType;
    className?: string;
}

export const PaymentSettings: React.FC<PaymentSettingsProps> = ({ setLoading, id, payableType, className }) => {
    const layoutContext = useContext(LayoutContext);
    const paymentMethodClient = useApiClient(PaymentMethodClient);
    const payableKey = useMemo<PayableKey>(() => new PayableKey({ id: id, payableType: payableType }), [id, payableType]);
    const [pamentMethods, setPamentMethods] = useState<PaymentMethodListViewModel | undefined>(undefined);
    const creditCard = useMemo<CreditCardPaymentMethodViewModel | undefined>(() => pamentMethods?.paymentMethods?.find(p => p instanceof CreditCardPaymentMethodViewModel) as CreditCardPaymentMethodViewModel | undefined, [pamentMethods])
    const bankAccount = useMemo<BankAccountPaymentMethodViewModel | undefined>(() => pamentMethods?.paymentMethods?.find(p => p instanceof BankAccountPaymentMethodViewModel) as BankAccountPaymentMethodViewModel | undefined, [pamentMethods])
    const [defaultPaymentMethod, setDefaultPaymentMethod] = useState<PaymentProviderType>(PaymentProviderType.BrainTree);
    const [isAddCreditCardVisible, setIsAddCreditCardVisible] = useState<boolean>(false);
    const [showPlaidLink, setShowPlaidLink] = useState<boolean>(false);
    const [paymentMethodToDelete, setPaymentMethodToDelete] = useState<PaymentMethodViewModel | undefined>(undefined);
    const [isRemovingPaymentProfile, setIsRemovingPaymentProfile] = useState(false);
    const toogleAddCreditCard = (e?: React.SyntheticEvent) => {
        if (e)
            e.preventDefault();
        setIsAddCreditCardVisible(!isAddCreditCardVisible);
    }

    const changeDefaultPaymentMethod = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedProvider = parseInt(e.target.value) as PaymentProviderType;
        setDefaultPaymentMethod(selectedProvider);
        paymentMethodClient.makeDefault(PaymentMethodRequest.fromJS({
            payableKey: payableKey,
            provider: selectedProvider
        })).catch(err => {
            layoutContext.showError(err);
        });
    }

    const onCCPaymentInfoChanged = () => {
        setIsAddCreditCardVisible(false);
        loadPaymentMethods();
    }

    const onPlaidLinkSuccess = () => {
        setShowPlaidLink(false);
        loadPaymentMethods();
    }

    const onPlaidLinkExit = () => {
        setShowPlaidLink(false);
    }

    const addBankAccount = (e?: React.SyntheticEvent) => {
        if (e)
            e.preventDefault();
        setShowPlaidLink(true);
    }
    const hideRemovePaymentMethodModal = (e) => {
        setPaymentMethodToDelete(undefined);
    }
    const onDeletePaymentMethod = (paymentMethod: PaymentMethodViewModel) => {
        setPaymentMethodToDelete(paymentMethod);
    }
    const deletePaymentMethod = () => {
        if (paymentMethodToDelete) {
            setIsRemovingPaymentProfile(true);
            paymentMethodClient.deletePaymentMethod(PaymentMethodRequest.fromJS({
                payableKey: payableKey,
                provider: paymentMethodToDelete.paymentProvider
            })).then(r => {
                setIsRemovingPaymentProfile(false);
                setPaymentMethodToDelete(undefined);
                setIsAddCreditCardVisible(false);
                loadPaymentMethods();
            }).catch(err => {
                setIsRemovingPaymentProfile(false);
                layoutContext.showError(err);
            })
        }
    }

    const loadPaymentMethods = async (abortSignal?: AbortSignal) => {
        setLoading(true);
        try {
            const paymentMethodsLoaded = await paymentMethodClient.paymentMethods(payableKey.payableType, payableKey.id, abortSignal);
            setPamentMethods(paymentMethodsLoaded);
            setDefaultPaymentMethod(paymentMethodsLoaded.paymentMethods?.find(f => f.isDefault)?.paymentProvider || PaymentProviderType.BrainTree);
        } catch (err: unknown) {
            if (abortSignal?.aborted) {
                if (typeof err == "string")
                    layoutContext.showMessage(err);
            } else {
                layoutContext.showError(err);
            }
        }
        setLoading(false);
    }

    useEffect(() => {
        const abortController = new AbortController();
        loadPaymentMethods(abortController.signal);
        return function cleanup() {
            abortController.abort();
        }
    }, []);

    const plaidLinkMethods = useBankAccount({
        payableKey,
        onSuccess: onPlaidLinkSuccess,
        onError: (err) => layoutContext.showError(err),
        onExit: onPlaidLinkExit
    })

    useEffect(() => {
        if (plaidLinkMethods.ready && showPlaidLink)
            plaidLinkMethods.open();
    }, [plaidLinkMethods.ready, showPlaidLink]);

    if (pamentMethods && pamentMethods.paymentMethodConfiguration.ccEntryDisabled)
        return null;

    return (<div className={classNames(classes.paymentsInformationWrapper, className)}>
        <div className={classes.paymentMethodWrapper}>
            <div className={classNames("h7", classes.subCaption)}>
                Credit Card
            </div>
            <div className={classNames({ "d-none": isAddCreditCardVisible })}>
            { !creditCard ?
                <>
                    <div>
                       No credit card on file.
                    </div>
                    <div>
                        <div className={classes.addCreditCardLink}>
                            <a href="#" onClick={toogleAddCreditCard} title="Add Credit Card">+ Add Credit Card</a>
                        </div>
                    </div>
                </>
                : <div className={classes.paymentInfo}>
                    <div className={classes.cardInfo}>
                    {creditCard.cardType} ending with <span style={{ fontWeight: 500 }}>*{creditCard.last4}</span>
                    </div>
                    <div className={classNames(classes.cardExpInfo, classes.expired)}>
                        Exp.{`${creditCard.expirationMonth}/${creditCard.expirationYear}`}
                    </div>
                    <div className={classes.paymentCardEditLink}>
                        <a href="#" onClick={toogleAddCreditCard}>Edit</a>
                    </div>
                </div>
            }
            </div>
            <div className={classes.addCreditCardFormWrapper}>
                { isAddCreditCardVisible && <PaymentMethodFormBrainTree payableKey={payableKey} editPaymentProfile={creditCard} onSuccess={onCCPaymentInfoChanged} onDeleteClick={(e) => { e.preventDefault(); creditCard && onDeletePaymentMethod(creditCard); }} onExit={toogleAddCreditCard} /> }
            </div>
            </div>
            { pamentMethods?.paymentMethodConfiguration.enableACHPayments && <div className={classes.paymentMethodWrapper}>
                <div className={classNames("h7", classes.subCaption)}>Bank Account</div>
                { !bankAccount ? <>
                    <div>
                        No bank account on file.
                    </div>
                    <div style={{ marginTop: 15 }}>
                        <a href="#" title="Add Bank Account" onClick={addBankAccount} className="add-bank-account-link">+ Add Bank Account</a>
                    </div>
                    <div style={{ marginTop: 15 }}>
                        Don't see your bank? Contact <a href="mailto:support@liquidspace.com">support@liquidspace.com</a> or call 1-855-257-2665 so we can help setup your payment profile
                    </div>
                </>
                : <>
                    <div className={classes.paymentInfo}>
                        <div className={classes.bankInfo}>
                            {bankAccount.bankName} ending with <span style={{ fontWeight: 500 }}>*{bankAccount.last4}</span><span style={{ color: "red" }}>{bankAccount.isVerified ? "" : " (Not verified)"}</span>
                        </div>
                        { pamentMethods.paymentMethodConfiguration.canDeleteBankAccount &&
                            <div className={classes.paymentDeleteLink}>
                                <a href="#" onClick={(e) => { e.preventDefault(); onDeletePaymentMethod(bankAccount); }} className="ocean-blue">Delete</a>
                            </div>
                        }
                        { bankAccount.isVerified &&
                            <div className={classes.paymentBankEditLink}>
                                <a href="#" onClick={addBankAccount} className="ocean-blue">Edit</a>
                            </div>
                        }
                    </div>
                    <div>
                        Bank account used for monthly or longer transactions.
                    </div>
                </> }
            </div>
            }
            { pamentMethods?.paymentMethodConfiguration.enableACHPayments && bankAccount && creditCard &&
                <div className={classes.paymentMethodWrapper}>
                    <div className={classNames("h7", classes.subCaption)}>Default</div>
                    <div className={classes.paymentInfo}>
                        <Input value={defaultPaymentMethod} onChange={changeDefaultPaymentMethod} type="select">
                            <option value={PaymentProviderType.Stripe}>Bank Account</option>
                            <option value={PaymentProviderType.BrainTree}>Credit Card</option>
                        </Input>
                    </div>
                </div>
            }
            {/* {payments?.userIsSuperUser &&
                    <div>
                        <div className={classNames("h7", classes.subCaption)}>Wire Transfer Payments</div>
                        <div>
                            <div className={classes.checkboxOptionWrapper}>
                                <div>
                                    <label className="checkbox-wrapper ">
                                        <CustomInput type="checkbox" id={user.id} checked={wireTransferPayments} />
                                    </label>
                                </div>
                                <div className="p1">
                                    <div>
                                        <span className="label">
                                            Enable Wire Transfer Payments
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                } */}
            { paymentMethodToDelete && <Modal isOpen={true} toggle={hideRemovePaymentMethodModal}>
                <ModalHeader toggle={hideRemovePaymentMethodModal}>Remove payment method?</ModalHeader>
                <ModalBody>
                    { paymentMethodToDelete instanceof CreditCardPaymentMethodViewModel && `Are you sure that you want to remove "${paymentMethodToDelete.cardType} ending with *${paymentMethodToDelete.last4}" payment method?` }
                    { paymentMethodToDelete instanceof BankAccountPaymentMethodViewModel && `Are you sure that you want to remove "${paymentMethodToDelete.bankName} account ending with *${paymentMethodToDelete.last4}" payment method?` }
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={hideRemovePaymentMethodModal}>Cancel</Button>
                    <SpinningButton color="primary" onClick={e => { e.preventDefault(); deletePaymentMethod(); }} isLoading={isRemovingPaymentProfile}>Remove</SpinningButton>
                </ModalFooter>
            </Modal>}
        </div>
    );
}